// apps/spa/src/lib/time.ts
// SPA-side wrappers that respect the user's 12h/24h preference (useTimeFormat).
//
// The shared `scheduleSummary` from @guardian/shared is intentionally NOT modified
// — the worker uses it for audit messages and we want the worker's display to stay
// canonical 24h. This file post-processes the string for SPA display only.

import { scheduleSummary } from '@guardian/shared';

/**
 * Renders the schedule summary for a reminder, swapping 24h HH:MM ET → h:MM AM/PM ET
 * when the user has chosen 12h. Returns the shared 24h summary as-is otherwise.
 *
 * The HH:MM ET pattern is the locked output shape of scheduleSummary for daily/weekly
 * presets ('Daily at 09:00 ET' / 'Weekly on Sunday at 18:00 ET'). Custom cron
 * expressions render via cronstrue and don't carry the ET suffix — those pass through.
 */
export function formatScheduleSummary(cron: string | null, hour12: boolean): string {
  const base = scheduleSummary(cron);
  if (!hour12) return base;
  return base.replace(/(\d{1,2}):(\d{2})\s*ET\b/, (_match, h: string, m: string) => {
    return `${formatHourMinute12(Number(h), m)} ET`;
  });
}

/**
 * Formats an absolute millisecond timestamp using the user's preference, anchored
 * in BASTN_TIMEZONE. Used by ReminderHistoryPanel + AuditLogRow tooltip.
 *
 * Default shape: `YYYY-MM-DD HH:MM ET` (24h) or `YYYY-MM-DD h:MM AM/PM ET` (12h).
 * Pass `includeSeconds` for finer-grained timestamps.
 */
export function formatAbsoluteTime(
  ts: number,
  hour12: boolean,
  opts: { timeZone: string; includeSeconds?: boolean } = { timeZone: 'America/New_York' },
): string {
  return (
    new Date(ts).toLocaleString('en-US', {
      timeZone: opts.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...(opts.includeSeconds ? { second: '2-digit' } : {}),
      hour12,
    }) + ' ET'
  );
}

function formatHourMinute12(h24: number, m: string): string {
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${m} ${period}`;
}
